import { Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";

const cellCls = "h-8 w-28 rounded-md border border-ib-steel/40 bg-ib-surface px-2 font-jetbrains text-xs text-ib-text text-right focus:border-ib-blue focus:outline-none";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—");

function PriceInput({ value, onChange, testId }) {
  return <input data-testid={testId} type="number" min="0" step="100" value={value} onChange={(e) => onChange(e.target.value)} className={cellCls} />;
}

export function PriceTable({ market, products, edits, onEdit, onDelete }) {
  const val = (key, field, fallback) => (edits[key]?.[field] !== undefined ? edits[key][field] : fallback);

  return (
    <div className="space-y-8">
      <section data-testid="market-price-table">
        <h3 className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase mb-3">Live ticker rates</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {market.map((m) => {
            const key = `m:${m.symbol}`;
            return (
              <div key={m.symbol} className={`rounded-lg border p-3 bg-ib-surface/50 ${edits[key] ? "border-ib-blue/60" : "border-ib-steel/20"}`} data-testid={`market-row-${m.symbol}`}>
                <div className="flex justify-between items-baseline mb-2">
                  <span className="font-outfit font-bold text-xs text-ib-text">{m.name}</span>
                  <span className="font-jetbrains text-[10px] text-ib-text-muted">{m.symbol}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-jetbrains text-xs text-ib-text-muted">₹</span>
                  <PriceInput testId={`market-price-${m.symbol}`} value={val(key, "price", m.price)} onChange={(v) => onEdit(key, "price", v)} />
                </div>
                <div className={`font-jetbrains text-[10px] mt-1.5 ${m.change >= 0 ? "text-ib-positive" : "text-ib-negative"}`}>
                  {m.change >= 0 ? "+" : ""}{m.change}% vs previous · {fmtDate(m.updated_at)}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section data-testid="product-price-table">
        <h3 className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase mb-3">Marketplace products ({products.length})</h3>
        <div className="rounded-xl border border-ib-steel/20 overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-ib-surface/70">
              <tr className="font-jetbrains text-[10px] text-ib-text-muted uppercase tracking-wide">
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3 hidden md:table-cell">Supplier</th>
                <th className="px-4 py-3 text-right">Price ₹/MT</th>
                <th className="px-4 py-3 text-right hidden sm:table-cell">Stock (MT)</th>
                <th className="px-4 py-3 hidden lg:table-cell">Updated</th>
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const key = `p:${p.id}`;
                return (
                  <tr key={p.id} className={`border-t border-ib-steel/10 ${edits[key] ? "bg-ib-blue/5" : ""}`} data-testid={`product-row-${p.id}`}>
                    <td className="px-4 py-2 font-jetbrains text-[11px] text-ib-text-muted">{p.id}</td>
                    <td className="px-4 py-2">
                      <div className="font-outfit font-semibold text-xs text-ib-text">{p.name}</div>
                      <div className="font-jetbrains text-[10px] text-ib-text-muted">{p.category} · {p.grade}</div>
                    </td>
                    <td className="px-4 py-2 font-manrope text-xs text-ib-text-secondary hidden md:table-cell">{p.supplier}</td>
                    <td className="px-4 py-2 text-right"><PriceInput testId={`product-price-${p.id}`} value={val(key, "price", p.price)} onChange={(v) => onEdit(key, "price", v)} /></td>
                    <td className="px-4 py-2 text-right hidden sm:table-cell"><PriceInput testId={`product-qty-${p.id}`} value={val(key, "quantity_available", p.quantity_available)} onChange={(v) => onEdit(key, "quantity_available", v)} /></td>
                    <td className="px-4 py-2 font-jetbrains text-[10px] text-ib-text-muted hidden lg:table-cell">{fmtDate(p.price_updated_at)}</td>
                    <td className="px-2 py-2 text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`delete-product-${p.id}`} className="p-1.5 rounded text-ib-text-muted hover:text-ib-negative hover:bg-ib-negative/10 transition-colors" aria-label={`Remove ${p.name}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-ib-surface border-ib-steel/30" data-testid="delete-product-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-outfit text-ib-text">Remove "{p.name}"?</AlertDialogTitle>
                            <AlertDialogDescription className="font-manrope text-ib-text-secondary text-xs">It will disappear from the marketplace immediately. This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="font-outfit text-xs">Cancel</AlertDialogCancel>
                            <AlertDialogAction data-testid="confirm-delete-product" onClick={() => onDelete(p.id)} className="bg-ib-negative hover:bg-ib-negative/90 text-white font-outfit text-xs">Remove</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
